import React, { useEffect, useRef, useState } from 'react';
import './ProgressRing.css';

// Animated circular readiness dial — pure SVG (no charting-library
// dependency needed for a single ring). Animates both the ring sweep and
// the number counting up on mount/value-change, which is the one number
// transition explicitly called out as good motion in the product brief.
// `value`/`max` are honest inputs — this never fabricates a percentage; if
// the caller has no value, don't render this component at all (handled by
// the caller, not here).
const ProgressRing = ({ value, max = 100, size = 96, strokeWidth = 9, color, trackColor = 'var(--z-border)', label, sublabel }) => {
  const clamped = Math.max(0, Math.min(max, value ?? 0));
  const pct = max > 0 ? clamped / max : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const [displayValue, setDisplayValue] = useState(0);
  const [dashOffset, setDashOffset] = useState(circumference);
  const rafRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setDisplayValue(clamped);
      setDashOffset(circumference * (1 - pct));
      return;
    }
    const duration = 900;
    const start = performance.now();
    const from = displayValue;
    const fromOffset = dashOffset;
    const toOffset = circumference * (1 - pct);

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out-cubic
      setDisplayValue(Math.round(from + (clamped - from) * eased));
      setDashOffset(fromOffset + (toOffset - fromOffset) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clamped, circumference]);

  return (
    <div className="z-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color || 'var(--z-violet)'} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="z-ring-center">
        <span className="z-ring-value">{displayValue}</span>
        {label && <span className="z-ring-label">{label}</span>}
      </div>
      {sublabel && <span className="z-ring-sublabel">{sublabel}</span>}
    </div>
  );
};

export default ProgressRing;
